import { ApiKycLogs } from "types/admin";
import { apiLogToVerificationStep } from "./adminKycVerificationUtils";

export interface PreparedAuditLogEntry extends ApiKycLogs {
  panComplianceLog?: ApiKycLogs;
}

const PARAM_LABELS: Record<string, string> = {
  panNumber: "PAN Number",
  bankNumber: "Bank Account Number",
  bankAccountNumber: "Bank Account Number",
  ifscode: "IFSC Code",
  name: "Name Provided",
  consent: "Consent",
  requestId: "Request ID",
  panHolderName: "Name on PAN",
  panValidityStatus: "PAN Validity",
  isCompliant: "Compliant",
  panOperationalStatus: "Operational Status",
  accountHolderName: "Account Holder",
  nameMatchScore: "Name Match Score",
  responseMessage: "Bank Response",
  status: "Status",
  message: "Message",
  rawResponse: "Response",
};

const HIDDEN_PARAM_KEYS = new Set(["requestId"]);

const SENSITIVE_PARAM_KEYS = new Set(["panNumber", "bankNumber", "bankAccountNumber"]);

export function isApiLogFailure(entry: ApiKycLogs): boolean {
  return apiLogToVerificationStep(entry)?.outcome === "FAILURE";
}

export function prepareAuditLogEntries(logs: ApiKycLogs[]): PreparedAuditLogEntry[] {
  const complianceLog = logs.find((entry) => entry.requestType === "PAN_COMPLIANCE");

  return logs
    .filter((entry) => entry.requestType !== "PAN_COMPLIANCE")
    .map((entry) =>
      entry.requestType === "PAN_VERIFICATION"
        ? { ...entry, panComplianceLog: complianceLog }
        : entry
    );
}

export function getParamLabel(key: string): string {
  return PARAM_LABELS[key] ?? key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

export function formatParamValue(key: string, value: string): string {
  if (key === "isCompliant") {
    if (value === "true") return "Yes";
    if (value === "false") return "No";
  }
  if (key === "nameMatchScore" && value && !value.endsWith("%")) {
    return `${value}%`;
  }
  if (key === "consent" && value === "Y") {
    return "Yes";
  }
  return value;
}

export function formatParamsForDisplay(
  params?: Record<string, string>,
  options?: { revealSensitive?: boolean }
) {
  if (!params) return [];

  const revealSensitive = options?.revealSensitive ?? false;

  return Object.entries(params)
    .filter(([key, value]) => {
      if (!value || HIDDEN_PARAM_KEYS.has(key)) return false;
      if (!revealSensitive && SENSITIVE_PARAM_KEYS.has(key)) return false;
      return true;
    })
    .map(([key, value]) => ({
      key,
      label: getParamLabel(key),
      value: formatParamValue(key, value),
    }));
}

export function parseHttpStatus(status?: string) {
  const code = Number.parseInt(status ?? "", 10);
  if (Number.isNaN(code)) {
    return { code: null, category: "unknown" as const, label: status || "Unknown" };
  }

  if (code >= 200 && code < 300) {
    return { code, category: "success" as const, label: `${code} OK` };
  }
  if (code >= 400 && code < 500) {
    return { code, category: "warning" as const, label: `${code} Client Error` };
  }
  if (code >= 500) {
    return { code, category: "error" as const, label: `${code} Server Error` };
  }
  return { code, category: "default" as const, label: String(code) };
}
